import { CaveAtmosphere } from "../../components/cave-atmosphere";
import { PlayerAvatar } from "../../components/player-avatar";
import { useId, useMemo, useState, type FormEvent, type ReactNode } from "react";

import { DisplayNameSchema, type GameCommand, type PlayerProjection } from "@saboteur/contracts";

import { QrPresentation } from "./display-link";
import "./lobby.css";

type JoinModeProps = {
  projection?: undefined;
  joinCode?: string;
  busy?: boolean;
  error?: string | undefined;
  onCreateRoom?: (displayName: string, avatarIndex: number) => void | Promise<void>;
  onJoinRoom?: (displayName: string, avatarIndex: number) => void | Promise<void>;
};

type WaitingRoomProps = {
  projection: PlayerProjection;
  joinCode: string;
  displayCode: string;
  busy?: boolean;
  error?: string | undefined;
  onCommand: (command: GameCommand) => void | Promise<void>;
};

export type LobbyScreenProps = JoinModeProps | WaitingRoomProps;

function nameError(displayName: string): string | undefined {
  const result = DisplayNameSchema.safeParse(displayName);
  if (result.success) return undefined;
  if (!displayName.trim()) return "Enter a display name.";
  return "Use a display name of 32 characters or fewer.";
}

function RoomEntry(props: JoinModeProps): ReactNode {
  const fieldId = useId();
  const [displayName, setDisplayName] = useState("");
  const [avatarIndex, setAvatarIndex] = useState(0);
  const [validationError, setValidationError] = useState<string>();
  const isCreate = props.joinCode === undefined;

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const error = nameError(displayName);
    setValidationError(error);
    if (error) return;

    const normalizedName = DisplayNameSchema.parse(displayName);
    void (isCreate
      ? props.onCreateRoom?.(normalizedName, avatarIndex)
      : props.onJoinRoom?.(normalizedName, avatarIndex));
  };

  return (
    <main className="lobby-page cave-scene entry-page">
      <CaveAtmosphere />
      <section className="lobby-card lobby-entry stack" aria-labelledby="lobby-entry-title">
        {!isCreate ? <p className="eyebrow">Room {props.joinCode}</p> : null}
        <h1 id="lobby-entry-title">{isCreate ? "Create room" : "Join room"}</h1>
        <form className="stack" onSubmit={submit} noValidate>
          <label className="lobby-label" htmlFor={fieldId}>
            Your name
          </label>
          <input
            id={fieldId}
            className="lobby-input"
            value={displayName}
            maxLength={32}
            autoComplete="nickname"
            enterKeyHint="done"
            disabled={props.busy}
            aria-describedby={`${fieldId}-help`}
            aria-invalid={Boolean(validationError)}
            onChange={(event) => {
              setDisplayName(event.target.value);
              if (validationError) setValidationError(nameError(event.target.value));
            }}
          />
          <p id={`${fieldId}-help`} className={validationError ? "lobby-error" : "lobby-help"}>
            {validationError ?? "1–32 characters"}
          </p>
          <fieldset className="avatar-picker" disabled={props.busy}>
            <legend>Your miner</legend>
            <div className="avatar-picker__grid">
              {[
                "Amber",
                "Jade",
                "Coral",
                "Violet",
                "Sky",
                "Rose",
                "Moss",
                "Copper",
                "Sage",
                "Silver",
              ].map((name, index) => (
                <label className="avatar-choice" key={name}>
                  <input
                    type="radio"
                    name={`${fieldId}-avatar`}
                    value={index}
                    checked={avatarIndex === index}
                    onChange={() => setAvatarIndex(index)}
                    aria-label={`${name} miner`}
                  />
                  <span className="avatar-choice__portrait">
                    <PlayerAvatar seat={index} />
                    <span className="avatar-choice__check" aria-hidden="true">
                      ✓
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          {props.error ? (
            <p className="lobby-error" role="alert">
              {props.error}
            </p>
          ) : null}
          <button type="submit" disabled={props.busy}>
            {props.busy ? "Connecting…" : isCreate ? "Create room" : "Join room"}
          </button>
        </form>
        {!isCreate ? (
          <a className="lobby-display-link" href="/">
            Try another room
          </a>
        ) : null}
      </section>
    </main>
  );
}

function WaitingRoom(props: WaitingRoomProps): ReactNode {
  const { public: room, self } = props.projection;
  const currentPlayer = room.participants.find(
    (participant) => participant.participantId === self.participantId,
  );
  const betweenRounds = room.phase === "BETWEEN_ROUNDS";
  const completed = room.phase === "COMPLETED";
  const returning = betweenRounds || completed;
  const canStart = betweenRounds
    ? self.legalCommands.canContinueRound
    : self.legalCommands.canStartMatch;
  const inviteUrl =
    room.joinUrl ?? new URL(`/room/${props.joinCode}`, window.location.origin).toString();
  const [shareStatus, setShareStatus] = useState("");
  const [copyFallback, setCopyFallback] = useState("");
  const displayUrl = new URL(
    `/display/${self.displayCode ?? props.displayCode}`,
    window.location.origin,
  ).toString();
  const copyLink = async (url: string, label: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyFallback("");
      setShareStatus(`${label} copied.`);
    } catch {
      setCopyFallback(url);
      setShareStatus("Select and copy the link below.");
    }
  };
  const sortedParticipants = useMemo(
    () => [...room.participants].sort((left, right) => left.seatIndex - right.seatIndex),
    [room.participants],
  );

  const setReady = (): void => {
    void props.onCommand({ type: "lobby.set-ready", ready: !currentPlayer?.ready });
  };

  return (
    <main className="lobby-page cave-scene waiting-page">
      <CaveAtmosphere />
      <section className="lobby-card waiting-card stack" aria-labelledby="waiting-room-title">
        <header className="lobby-header">
          <div>
            <h1 id="waiting-room-title">Waiting room</h1>
          </div>
          <div className="room-code" aria-label={`Room code ${props.joinCode}`}>
            <span>Room code</span>
            <strong>{props.joinCode}</strong>
          </div>
        </header>

        {returning ? (
          <div className="lobby-round-summary stack">
            <h2>{completed ? "Game complete" : `Round ${room.roundNumber} complete`}</h2>
            <p className="lobby-help">
              {completed
                ? "Ready for another game with this crew?"
                : `Gold shared. Round ${room.roundNumber + 1} is next.`}
            </p>
            {completed ? (
              <ol className="lobby-standings" aria-label="Final gold standings">
                {[...(room.finalResults ?? [])]
                  .sort((a, b) => b.goldTotal - a.goldTotal)
                  .map((result) => (
                    <li key={result.participantId}>
                      <span>
                        {
                          room.participants.find(
                            (player) => player.participantId === result.participantId,
                          )?.displayName
                        }
                        {result.winner ? " · Winner" : ""}
                      </span>
                      <strong>{result.goldTotal} gold</strong>
                    </li>
                  ))}
              </ol>
            ) : (
              <p>
                Your gold:{" "}
                <strong>{self.goldAwards.reduce((sum, card) => sum + card.value, 0)}</strong>
              </p>
            )}
          </div>
        ) : null}
        {room.phase === "LOBBY" ? (
          <div className="join-invite">
            <QrPresentation value={inviteUrl} label="Scan to join this room" />
            <div className="stack">
              <p>Invite your crew</p>
              <div className="invite-actions">
                <button type="button" onClick={() => void copyLink(inviteUrl, "Invite link")}>
                  Copy invite
                </button>
                {currentPlayer?.isHost ? (
                  <button
                    className="lobby-secondary"
                    type="button"
                    title="Copy a read-only link for your TV"
                    onClick={() => void copyLink(displayUrl, "Display link")}
                  >
                    Copy display link
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
        <p className="lobby-help" aria-live="polite">
          {shareStatus}
        </p>
        {copyFallback ? (
          <input
            className="lobby-input"
            aria-label="Link to copy"
            value={copyFallback}
            readOnly
            onFocus={(event) => event.currentTarget.select()}
          />
        ) : null}

        <section className="stack" aria-labelledby="crew-title">
          <div className="lobby-section-title">
            <h2 id="crew-title">Players</h2>
            <span>{sortedParticipants.length}/10</span>
          </div>
          <ol className="seat-list">
            {sortedParticipants.map((participant) => (
              <li className="seat" key={participant.participantId}>
                <span
                  className={`presence-dot ${participant.connected ? "is-online" : ""}`}
                  aria-label={participant.connected ? "Connected" : "Disconnected"}
                />
                <span className="seat-name">
                  <PlayerAvatar seat={participant.avatarIndex ?? participant.seatIndex} />
                  {participant.displayName}
                  {participant.participantId === self.participantId ? " (you)" : ""}
                </span>
                {participant.isHost ? <span className="seat-badge">Host</span> : null}
                <strong
                  className="seat-gold"
                  aria-label={`${participant.displayName}: ${participant.goldTotal ?? participant.finalGoldTotal ?? "unknown"} gold`}
                >
                  {participant.goldTotal ?? participant.finalGoldTotal ?? "—"} gold
                </strong>
                {!returning ? (
                  <span className={`ready-state ${participant.ready ? "is-ready" : ""}`}>
                    {participant.ready ? "Ready" : "Not ready"}
                  </span>
                ) : null}
              </li>
            ))}
          </ol>
        </section>

        {props.error ? (
          <p className="lobby-error" role="alert">
            {props.error}
          </p>
        ) : null}

        {sortedParticipants.length === 2 ? (
          <p className="lobby-help">2-player house rules · Best with 3–10</p>
        ) : null}
        <footer className="lobby-actions">
          {returning && currentPlayer?.isHost ? (
            <button
              className="lobby-secondary"
              type="button"
              onClick={() => void copyLink(displayUrl, "Display link")}
            >
              Copy display link
            </button>
          ) : null}
          {!returning ? (
            <button
              className={currentPlayer?.ready ? "lobby-secondary" : ""}
              type="button"
              disabled={props.busy}
              onClick={setReady}
            >
              {currentPlayer?.ready ? "Not ready" : "Ready"}
            </button>
          ) : null}
          {currentPlayer?.isHost ? (
            <button
              type="button"
              disabled={props.busy || !canStart}
              aria-describedby="start-match-help"
              onClick={() =>
                void props.onCommand({ type: betweenRounds ? "round.continue" : "match.start" })
              }
            >
              {betweenRounds
                ? `Start round ${room.roundNumber + 1}`
                : completed
                  ? "Start new game"
                  : "Start game"}
            </button>
          ) : null}
        </footer>
        {currentPlayer?.isHost ? (
          <p id="start-match-help" className="lobby-help">
            {returning
              ? "Start when your crew is ready."
              : canStart
                ? "Everyone is ready."
                : room.participants.length < 2
                  ? "Invite one more player to start."
                  : `Waiting for ${room.participants
                      .filter((p) => !p.ready)
                      .map((p) => p.displayName)
                      .join(", ")} to get ready.`}
          </p>
        ) : (
          <p className="lobby-help">
            {returning
              ? "Waiting for the host to start."
              : "The host starts when 2–10 players are ready."}
          </p>
        )}
      </section>
    </main>
  );
}

export function LobbyScreen(props: LobbyScreenProps): ReactNode {
  return props.projection ? <WaitingRoom {...props} /> : <RoomEntry {...props} />;
}
